/**
 * The search in the console bar: a field-shaped button that opens a palette of everywhere
 * the console can take you.
 *
 * It replaced the page title the bar used to carry. Every screen already names itself in its
 * own heading, so the bar was saying the same word twice; a way to *reach* a page is worth
 * that space more than a second copy of where you already are.
 *
 * It searches the destinations the sidebar offers this person — the caller passes them in,
 * so the palette can never list a page the sidebar hides (and the page would answer 403).
 * `⌘K` / `Ctrl+K` opens it from anywhere in the console, next to the sidebar's own `⌘B`.
 */
import type { LucideIcon } from 'lucide-react'
import { CornerDownLeft, Search } from 'lucide-react'
import { type KeyboardEvent, type ReactNode, useEffect, useId, useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog'
import { cn } from '../lib/utils'

export interface SearchDestination {
	/** Where the entry goes, and its key in the list. An entry with `onSelect` still needs a unique one. */
	to: string
	label: string
	group: string
	icon: LucideIcon
	/** Drawn instead of `icon` when given, e.g. an organisation's initials tile. */
	mark?: ReactNode
	/** Runs instead of navigating to `to`, e.g. switching organisation. */
	onSelect?: () => void
	/** Other words somebody might type for this page — "people" for Coverage, "plan" for Billing. */
	keywords?: readonly string[]
}

const SHORTCUT = 'k'

/**
 * How well `query` matches a destination: lower is better, `null` is no match.
 *
 * A label that starts with the query beats one that merely contains it, and either beats a
 * keyword hit, so typing "ro" puts Roles above Rules' "routing" keyword without a fuzzy-match
 * library deciding it for us.
 */
function rank(destination: SearchDestination, query: string): number | null {
	const label = destination.label.toLowerCase()
	if (label.startsWith(query)) return 0
	if (label.split(/\s+/).some((word) => word.startsWith(query))) return 1
	if (label.includes(query)) return 2
	if (destination.keywords?.some((keyword) => keyword.toLowerCase().startsWith(query))) return 3
	if (destination.group.toLowerCase().startsWith(query)) return 4
	return null
}

export function filterDestinations(destinations: readonly SearchDestination[], query: string): SearchDestination[] {
	const needle = query.trim().toLowerCase()
	if (needle === '') return [...destinations]

	return destinations
		.map((destination, index) => ({ destination, index, score: rank(destination, needle) }))
		.filter((entry): entry is { destination: SearchDestination; index: number; score: number } => entry.score !== null)
		.sort((a, b) => a.score - b.score || a.index - b.index)
		.map((entry) => entry.destination)
}

function isMac(): boolean {
	return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
}

const NO_RESULTS: readonly SearchDestination[] = []

export function GlobalSearch({
	destinations,
	navigate,
	className,
	label = 'Search the console',
	placeholder = 'Search pages, settings and organisations…',
	found = NO_RESULTS,
	onQueryChange,
}: {
	destinations: readonly SearchDestination[]
	navigate: (to: string) => void
	className?: string
	/** Names the palette for assistive technology. */
	label?: string
	placeholder?: string
	/** Records found for the query elsewhere, e.g. on a server. Listed after the pages, as they come. */
	found?: readonly SearchDestination[]
	/** Hears every change to the query, so the caller can look records up for `found`. */
	onQueryChange?: (query: string) => void
}) {
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState('')
	const [active, setActive] = useState(0)
	const listId = useId()

	const results = useMemo(() => [...filterDestinations(destinations, query), ...found], [destinations, query, found])

	useEffect(() => {
		function onKeyDown(event: globalThis.KeyboardEvent) {
			if (event.key.toLowerCase() === SHORTCUT && (event.metaKey || event.ctrlKey)) {
				event.preventDefault()
				setOpen((previous) => !previous)
			}
		}
		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
	}, [])

	function changeOpen(next: boolean) {
		setOpen(next)
		if (!next) {
			setQuery('')
			onQueryChange?.('')
			setActive(0)
		}
	}

	function go(destination: SearchDestination | undefined) {
		if (destination === undefined) return
		changeOpen(false)
		if (destination.onSelect) destination.onSelect()
		else navigate(destination.to)
	}

	function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
		if (results.length === 0) return
		if (event.key === 'ArrowDown') {
			event.preventDefault()
			setActive((index) => (index + 1) % results.length)
		} else if (event.key === 'ArrowUp') {
			event.preventDefault()
			setActive((index) => (index - 1 + results.length) % results.length)
		} else if (event.key === 'Enter') {
			event.preventDefault()
			go(results[Math.min(active, results.length - 1)])
		}
	}

	const shortcutLabel = isMac() ? '⌘K' : 'Ctrl K'
	const activeIndex = Math.min(active, results.length - 1)
	const browsing = query.trim() === ''

	return (
		<>
			<button
				type="button"
				onClick={() => changeOpen(true)}
				aria-keyshortcuts="Meta+K Control+K"
				className={cn(
					'flex h-8 w-full max-w-sm min-w-0 items-center gap-2 rounded-md border border-input bg-card px-2.5 text-sm text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/40',
					className,
				)}
			>
				<Search className="size-4 shrink-0" aria-hidden />
				<span className="truncate">Search…</span>
				<kbd className="ml-auto hidden rounded border border-border bg-muted px-1.5 font-sans text-[11px] leading-5 text-muted-foreground sm:inline">
					{shortcutLabel}
				</kbd>
			</button>

			<Dialog open={open} onOpenChange={changeOpen}>
				<DialogContent showCloseButton={false} className="top-[20%] translate-y-0 gap-0 overflow-hidden p-0 sm:max-w-lg">
					<DialogTitle className="sr-only">{label}</DialogTitle>
					<DialogDescription className="sr-only">Type to find what you need, then press Enter to open it.</DialogDescription>

					<div className="flex items-center gap-2 border-b border-border px-3">
						<Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
						<input
							autoFocus
							role="combobox"
							aria-expanded
							aria-controls={listId}
							aria-activedescendant={results.length > 0 ? `${listId}-${activeIndex}` : undefined}
							aria-autocomplete="list"
							aria-label={label}
							placeholder={placeholder}
							value={query}
							onChange={(event) => {
								setQuery(event.target.value)
								onQueryChange?.(event.target.value)
								setActive(0)
							}}
							onKeyDown={onInputKeyDown}
							className="h-11 w-full min-w-0 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
						/>
					</div>

					<div id={listId} role="listbox" aria-label="Results" className="max-h-80 overflow-y-auto p-1.5">
						{results.length === 0 ? (
							<p className="px-3 py-6 text-center text-sm text-muted-foreground">Nothing matches “{query.trim()}”.</p>
						) : (
							results.map((destination, index) => {
								const selected = index === activeIndex
								// Unfiltered, the list reads as the sidebar does, under its group headings. Once
								// ranked, neighbours can come from different groups, so the group moves beside
								// each result instead of heading a run of one.
								const startsGroup = browsing && (index === 0 || results[index - 1]?.group !== destination.group)
								return (
									<div key={destination.to}>
										{startsGroup && (
											<div className="px-2 pt-2 pb-1 text-xs font-medium text-muted-foreground" aria-hidden>
												{destination.group}
											</div>
										)}
										{/* biome-ignore lint/a11y/useKeyWithClickEvents: keys belong to the combobox input, which points at this option through aria-activedescendant — the WAI-ARIA combobox pattern, where focus never leaves the field. */}
										<div
											id={`${listId}-${index}`}
											role="option"
											aria-selected={selected}
											tabIndex={-1}
											onMouseMove={() => setActive(index)}
											onClick={() => go(destination)}
											className={cn(
												'flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm text-foreground',
												selected && 'bg-muted',
											)}
										>
											{/* One fixed slot, so a label sits at the same indent whether it has an icon or a mark. */}
											<span className="grid size-5 shrink-0 place-items-center">
												{destination.mark ?? (
													<destination.icon className="size-4 text-muted-foreground" aria-hidden />
												)}
											</span>
											<span className="truncate">{destination.label}</span>
											{!browsing && <span className="text-xs text-muted-foreground">{destination.group}</span>}
											{selected && <CornerDownLeft className="ml-auto size-3.5 text-muted-foreground" aria-hidden />}
										</div>
									</div>
								)
							})
						)}
					</div>
				</DialogContent>
			</Dialog>
		</>
	)
}
